import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, SvgIcon } from '@salecycle/ui-component-library';
import { DisplaySegmentationConfiguration } from './components/DisplaySegmentationConfiguration';
import { useConvertMessageToSplitTest } from './hooks/useConvertMessageToSplitTest';
import { SelectTemplateControl } from './components/SharedDrawerControls/SelectTemplateControl';
import { DisplayWebContentName } from './components/DisplayWebContentName';
import ControlGroup from './components/ControlGroup';
import { Triggers } from '../Trigger/components/Triggers';
import { useMessageTriggers } from './hooks/useMessageTriggers';
import { SchedulingControl } from '../../SchedulingControl';
import { Canvas } from '../../../../../domain/Canvas/Canvas';
import { useWorkFlowCanvas } from '../../../contexts/CampaignFlowProvider';

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

export interface DisplayWebContentDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  messageId: string;
}

export function DisplayWebContentDrawer({ messageId, open, closeDrawer }: DisplayWebContentDrawerProps) {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const convertToSplitTest = useConvertMessageToSplitTest({ id: messageId });

  const { setTrigger, triggers } = useMessageTriggers(messageId);

  const { state: workflowState } = useWorkFlowCanvas();
  let hasBranchNodes = false;
  if (workflowState.nodes.length && workflowState.edges.length) {
    hasBranchNodes = Canvas.createFromState(workflowState).hasBranchNodes();
  }

  return (
    <Drawer
      header={t('onsite.editor.workflow.displayWebContentMessage.drawer.title')}
      footer={
        <Button
          variation="secondary"
          onClick={closeDrawer}
          fullWidth
          ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.drawer.closeButtonAria')}
        >
          {t('onsite.editor.workflow.displayWebContentMessage.drawer.closeButton')}
        </Button>
      }
      onClose={closeDrawer}
      open={open}
    >
      <div style={{ marginBottom: theme.designToken.spacing4 }}>
        <ControlGroup messageId={messageId} key={messageId} isSplitTest={false} />
      </div>

      <DrawerContent>
        <DisplayWebContentName id={messageId} key={messageId} />

        {hasBranchNodes && <SchedulingControl messageId={messageId} />}

        {triggers && (
          <Triggers
            title={t('onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.messageSettings.title')}
            description={t(
              'onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.messageSettings.tooltip',
            )}
            triggers={triggers}
            onTriggerChanged={setTrigger}
            id={`message-${messageId}`}
          />
        )}

        <SelectTemplateControl messageId={messageId} />

        <DisplaySegmentationConfiguration id={messageId} />

        <Button
          leftSideIcon={<SvgIcon name="vials" />}
          variation="secondary"
          aria-label={t('onsite.editor.workflow.displayWebContentMessage.drawer.createSplitTestButtonAria')}
          onClick={convertToSplitTest}
          // Need to set the width to be fit-content due to flexbox
          style={{ width: 'fit-content' }}
        >
          {t('onsite.editor.workflow.displayWebContentMessage.drawer.createSplitTestButtonLabel')}
        </Button>
      </DrawerContent>
    </Drawer>
  );
}
